import { User as FirebaseUser } from 'firebase/auth';

import { type PlatformEntitlements, type PlatformSubscription, PlatformSubscriptionsFacade } from 'lib/platform';
import { isStripe } from 'modules/subscription/utils/checkers';
import { getExtensionId } from 'utils/extension';

import type { WordsListenedDataBySource } from './settings';

export type ExtensionSettings = {
  playbackSpeed?: number;
  autoSpeedUp?: boolean;
  voice?: {
    displayName: string;
    engine: string;
    gender: string;
    languageCode: string;
    name: string;
  };
};

export type UsageStats = {
  stats: WordsListenedDataBySource;
  expires: number;
};

export type UserData = {
  // Required
  firebaseUser: FirebaseUser;

  // Optional
  entitlements?: PlatformEntitlements;
  subscription?: PlatformSubscription;
  usage?: UsageStats;
  firstPdfDocument?: string;
  displayNameOverride?: string;
};

export class User {
  private data: UserData;

  constructor(data: UserData) {
    if (data.displayNameOverride === data.firebaseUser.displayName) {
      data = { ...data };
      delete data.displayNameOverride;
    }

    this.data = data;
  }

  update(update: Partial<UserData> | ((oldData: UserData) => Partial<UserData>)) {
    const newPartialData = typeof update === 'function' ? update(this.data) : update;
    return new User({
      ...this.data,
      ...newPartialData
    });
  }

  async getBillingDashboardUrl() {
    if (!isStripe(this)) return undefined;

    const extensionId = await getExtensionId();
    return (await PlatformSubscriptionsFacade.singleton.getBillingDashboardUrl(extensionId).catch(() => undefined)) ?? undefined;
  }

  get firebaseUser() {
    return this.data.firebaseUser;
  }
  get entitlements() {
    return this.data.entitlements;
  }
  get subscription() {
    return this.data.subscription;
  }
  get usage() {
    return this.data.usage;
  }
  get firstPdfDocument() {
    return this.data.firstPdfDocument;
  }

  // Derived properties
  get displayName() {
    return this.data.displayNameOverride ?? this.firebaseUser.displayName;
  }
  get email() {
    return this.firebaseUser.email;
  }
  get uid() {
    return this.firebaseUser.uid;
  }
  get metadata() {
    return this.firebaseUser.metadata;
  }
  get emailVerified() {
    return this.firebaseUser.emailVerified;
  }
  get isAnonymous() {
    return this.firebaseUser.isAnonymous || (!this.firebaseUser.email && !this.firebaseUser.phoneNumber && (this.firebaseUser.providerData?.length ?? 0) === 0);
  }
  get photoURL() {
    return this.firebaseUser.photoURL;
  }
}
